import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core"
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import {
  TrashIcon,
  EllipsisVerticalIcon,
  Cog6ToothIcon,
  ChevronRightIcon
  // EyeIcon,
  // EyeSlashIcon
} from "@heroicons/react/24/outline"
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/solid"
import React, { useState, useEffect } from "react"

import { Button } from "../../components/shared/Buttons"
import { Input, TextArea, Select } from "../../components/shared/Inputs"
import { Box } from "../../components/shared/Layout"
import { AnimatedModal } from "../../components/shared/Modal"
import Typography from "../../components/shared/Typography"

import { useWebsiteBuilderV2 } from "./WebsiteBuilderV2Context"

// Utility function to scroll to a section in the preview

const scrollToSection = (sectionId, sectionLayout, layoutVariant = null) => {
  // Build section anchor ID matching the format in SectionRenderer
  const sectionAnchorId =
    sectionLayout === "events"
      ? "events-section-full"
      : layoutVariant
      ? `section-${sectionLayout}-${layoutVariant}-${sectionId}`
      : `section-${sectionLayout}-${sectionId}`

  const sectionElement = document.getElementById(sectionAnchorId)

  if (sectionElement) {
    // Use a simpler approach that doesn't interfere with the container's styling
    // Just scroll the section into view with smooth behavior
    sectionElement.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest"
    })

    // Add a temporary highlight effect
    sectionElement.style.transition = "box-shadow 0.3s ease"
    sectionElement.style.boxShadow = "0 0 0 4px rgba(20, 184, 166, 0.3)"

    setTimeout(() => {
      sectionElement.style.boxShadow = ""
    }, 2000)

    return true
  }

  return false
}

// Default page types that should not show edit/delete icons
const DEFAULT_PAGE_TYPES = ["home", "services", "reviews", "about", "contact"]

// Helper function to check if a page is a default page
const isDefaultPage = (page) => page.defaultPageType && DEFAULT_PAGE_TYPES.includes(page.defaultPageType)

// Reserved slugs that cannot be used as page URLs
const RESERVED_SLUGS = [
  "events",
  "services",
  "checkout",
  "login",
  "logout",
  "admin",
  "new",
  "edit",
  "index",
  "session",
  "users",
  "stylesheets",
  "assets",
  "javascripts",
  "images",
  "articles",
  "specialties",
  "wellness",
  "privacy",
  "terms",
  "robots",
  "about",
  "questions_az",
  "practitioners_az",
  "demo_confirmed",
  "application_completed",
  "download_app",
  "lifer_checkout",
  "business_associate_agreement",
  "proplatform",
  "book_demo",
  "home",
  "pro",
  "pro2",
  "pro3",
  "apply",
  "lp",
  "confirm",
  "intake_forms",
  "practitioner-search",
  "search",
  "m",
  "c",
  "r",
  "webhooks"
]

// Helper function to convert backend layout to UX section type name
const getSectionTypeName = (layout) => {
  const layoutToSectionType = {
    hero: "Hero",
    sub_hero: "Hero",
    services: "Services",
    featured_services: "Featured Services",
    gallery: "Gallery",
    reviews: "Reviews",
    about: "About",
    cta_banner: "Banner",
    cta_content: "Content",
    faq: "FAQ",
    features: "Keywords",
    quote: "Quote",
    packages: "Packages",
    keywords: "Keywords",
    events: "Events",
    credentials: "Credentials",
    contact: "Contact"
  }
  return layoutToSectionType[layout] || layout.charAt(0).toUpperCase() + layout.slice(1).replace(/_/g, " ")
}

const SortableSectionItem = ({
  section,
  isSelected,
  onEdit,
  // onDelete,
  // onToggleHidden,
  isLast = false,
  isDefaultPage = false,
  sectionIndex = 0,
  pageSections = []
}) => {
  const isHeroSection = section.layout === "hero" || section.layout === "sub_hero"

  // Prevent dragging Hero section on default pages
  const sortableOptions = {
    id: section.id,
    disabled: isHeroSection && isDefaultPage
  }

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable(sortableOptions)

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  }

  const handleSectionClick = () => {
    scrollToSection(section.id, section.layout, section.settings?.layout)
    onEdit(section)
  }

  // Get a more descriptive title for repeated sections
  const getSectionDisplayTitle = () => {
    const baseTitle = getSectionTypeName(section.layout)

    // Count how many sections of the same type come before this one
    const sameTypeSections = pageSections.filter((s, idx) => s.layout === section.layout && idx < sectionIndex)

    // If there are previous sections of the same type, add a number
    if (sameTypeSections.length > 0) {
      return `${baseTitle} ${sameTypeSections.length + 1}`
    }

    return baseTitle
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center justify-between ${
        isHeroSection ? "px-10" : "px-1"
      } cursor-pointer py-4 transition-colors ${!isLast ? "border-b border-gray-light" : ""} ${
        isSelected ? "bg-teal/10" : section.hidden ? "border-orange bg-orange/5 opacity-60" : "hover:bg-gray-ultralight"
      } ${isDragging ? "opacity-50" : ""} ${isHeroSection && isDefaultPage ? "bg-gray-50" : ""}`}
      onClick={handleSectionClick}>
      <div className="flex items-center gap-3">
        {!isHeroSection && (
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab rounded p-1 text-gray hover:text-gray-dark active:cursor-grabbing"
            title="Drag to reorder">
            <EllipsisVerticalIcon className="h-4 w-4" />
          </button>
        )}
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Typography variant="body" className="font-medium">
              {getSectionDisplayTitle()}
            </Typography>
            {section.hidden && (
              <span className="rounded bg-orange/10 px-2 py-0.5 text-xs font-medium text-orange-dark">(hidden)</span>
            )}
          </div>
          {section.headline && (
            <Typography variant="small" className="text-gray">
              {section.headline}
            </Typography>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1">
        {/* Edit section - for all pages */}
        <button
          onClick={(e) => {
            e.stopPropagation()
            scrollToSection(section.id, section.layout, section.settings?.layout)
            onEdit(section)
          }}
          className="rounded p-1 text-gray hover:text-gray-dark"
          title="Edit section">
          <ChevronRightIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}

const SectionForm = ({ section, availableLayouts, onSubmit, onCancel, isEditing = false }) => {
  const [formData, setFormData] = useState({
    layout: section?.layout || "",
    headline: section?.headline || "",
    subheadline: section?.subheadline || "",
    body: section?.body || "",
    imageUrl: section?.imageUrl || ""
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    onSubmit(formData)
  }

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  return (
    <Box className="mb-4 border border-gray-light p-4">
      <Typography variant="h5" className="mb-3">
        {isEditing ? "Edit Section" : "Add New Section"}
      </Typography>
      <form onSubmit={handleSubmit} className="space-y-3">
        <Select
          label="Section Type"
          value={formData.layout}
          onChange={(e) => handleChange("layout", e.target.value)}
          required
          disabled={isEditing && section?.layout === "hero"}>
          <option value="">Select section type</option>
          {availableLayouts.map((layout) => (
            <option key={layout.value} value={layout.value}>
              {layout.label}
            </option>
          ))}
        </Select>
        <Input
          label="Headline"
          value={formData.headline}
          onChange={(e) => handleChange("headline", e.target.value)}
          placeholder="Enter headline"
        />
        <Input
          label="Subheadline"
          value={formData.subheadline}
          onChange={(e) => handleChange("subheadline", e.target.value)}
          placeholder="Enter subheadline"
        />
        <TextArea
          label="Content"
          value={formData.body}
          onChange={(e) => handleChange("body", e.target.value)}
          placeholder="Enter content"
          rows={4}
        />
        <Input
          label="Image URL"
          value={formData.imageUrl}
          onChange={(e) => handleChange("imageUrl", e.target.value)}
          placeholder="Enter image URL"
        />
        <div className="flex gap-2">
          <Button type="primary" htmlType="submit">
            {isEditing ? "Update Section" : "Add Section"}
          </Button>
          <Button type="secondary" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Box>
  )
}

const CreatePageForm = ({ onSubmit, onCancel, isInModal = false }) => {
  const [title, setTitle] = useState("")
  const [slug, setSlug] = useState("")
  const [slugError, setSlugError] = useState("")

  const handleSlugChange = (value) => {
    setSlug(value)
    if (RESERVED_SLUGS.includes(value.toLowerCase())) {
      setSlugError(`"${value}" is reserved and cannot be used as a page URL`)
    } else {
      setSlugError("")
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (title.trim() && !slugError) {
      onSubmit(title.trim(), slug.trim() || null)
      setTitle("")
      setSlug("")
      setSlugError("")
    }
  }

  const isFormValid = title.trim() && !slugError

  return (
    <form id="create-page-form" onSubmit={handleSubmit} className="space-y-4">
      <Input
        label="Page Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Enter page title"
        required
      />
      <Input
        label="Slug (optional)"
        value={slug}
        onChange={(e) => handleSlugChange(e.target.value)}
        placeholder="auto-generated from title"
        explanatorySubtext={slugError ? undefined : "Leave empty to auto-generate from title"}
        error={slugError}
      />
      {!isInModal && (
        <div className="flex gap-2">
          <Button type="primary" htmlType="submit" disabled={!isFormValid}>
            Create Page
          </Button>
          <Button type="secondary" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      )}
    </form>
  )
}

const EditPageForm = ({ page, onSubmit, onCancel, isInModal = false }) => {
  const isHomePage = page.defaultPageType === "home"
  const [title, setTitle] = useState(page.title)
  const [slug, setSlug] = useState(page.slug)
  const [slugError, setSlugError] = useState("")

  // If this page has a defaultPageType, allow it to use its corresponding slug
  // For example, a page with defaultPageType "services" can use "services" as its slug
  const reservedSlugs = page.defaultPageType
    ? RESERVED_SLUGS.filter((reservedSlug) => reservedSlug !== page.defaultPageType)
    : RESERVED_SLUGS

  const handleSlugChange = (value) => {
    setSlug(value)
    if (reservedSlugs.includes(value.toLowerCase())) {
      setSlugError(`"${value}" is reserved and cannot be used as a page URL`)
    } else {
      setSlugError("")
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    // For home page, only allow title changes, not slug changes
    if (isHomePage) {
      const titleChanged = title.trim() !== page.title
      if (titleChanged && title.trim()) {
        // Keep the original slug for home page
        onSubmit(page.slug, title.trim())
      }
      return
    }

    const titleChanged = title.trim() !== page.title
    const slugChanged = slug.trim() !== page.slug

    if ((titleChanged || slugChanged) && title.trim() && !slugError) {
      onSubmit(slug.trim(), title.trim())
    }
  }

  const isFormValid =
    title.trim() && (title.trim() !== page.title || (!isHomePage && slug.trim() !== page.slug)) && !slugError

  return (
    <form id="edit-page-form" onSubmit={handleSubmit} className="space-y-4">
      <Input
        label="Page Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Enter page title"
        required
      />
      <Input
        label="Page URL / Slug"
        value={slug}
        onChange={(e) => handleSlugChange(e.target.value)}
        placeholder="Enter page URL"
        explanatorySubtext={
          isHomePage
            ? "The home page URL cannot be changed"
            : slugError
            ? undefined
            : "This will be the URL of your page"
        }
        error={slugError}
        required
        disabled={isHomePage}
      />
      {!isInModal && (
        <div className="flex gap-2">
          <Button type="primary" htmlType="submit" disabled={!isFormValid}>
            Update Page
          </Button>
          <Button type="secondary" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      )}
    </form>
  )
}

const PageAccordion = ({
  page,
  isActive,
  isLoading: isPageLoading,
  onToggle,
  onEdit,
  onDelete,
  onSelectPage
  // onToggleHidden
}) => {
  const {
    sections,
    currentPage,
    selectedSection,
    availableLayouts,
    handleCreateSection,
    handleDeleteSection,
    handleMoveSection,
    handleUpdateSection,
    isLoading,
    setShowAddSectionScreen,
    setShowEditSectionScreen,
    setEditingSection
  } = useWebsiteBuilderV2()

  const [showAddSectionForm, setShowAddSectionForm] = useState(false)
  const [showEditPageModal, setShowEditPageModal] = useState(false)
  const [pageSections, setPageSections] = useState([])

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  const isDefaultPageCheck = isDefaultPage(page)
  const isCurrentPage = currentPage?.id === page.id

  // Update page sections when sections change in context
  useEffect(() => {
    if (currentPage?.id === page.id) {
      setPageSections(sections)
    }
  }, [currentPage?.id, page.id, sections])

  const handleSelectSection = (section) => {
    // Open the section editor when a section is clicked
    setEditingSection(section)
    setShowEditSectionScreen(true)
    setShowAddSectionForm(false)
  }

  const handleAddSubmit = async (formData) => {
    try {
      // First select this page if it's not the current page
      if (currentPage?.id !== page.id) {
        onSelectPage(page)
        // Wait a bit for the page to be selected and sections to load
        await new Promise((resolve) => setTimeout(resolve, 100))
      }

      await handleCreateSection(formData.layout, formData)
      setShowAddSectionForm(false)
    } catch (error) {
      // Error handling is done in the context
    }
  }

  const handleDeleteClick = async (sectionId) => {
    if (window.confirm("Are you sure you want to delete this section?")) {
      try {
        await handleDeleteSection(sectionId)
      } catch (error) {
        // Error handling is done in the context
      }
    }
  }

  const handleEditClick = (section) => {
    setEditingSection(section)
    setShowEditSectionScreen(true)
    setShowAddSectionForm(false)
  }

  const handleToggleHidden = async (sectionId, hidden) => {
    try {
      await handleUpdateSection(sectionId, { hidden })
    } catch (error) {
      console.error("Error toggling hidden:", error) // eslint-disable-line no-console
    }
  }

  const handleDragEnd = async (event) => {
    const { active, over } = event

    if (active && over && active.id !== over.id) {
      const oldIndex = pageSections.findIndex((section) => section.id === active.id)
      const newIndex = pageSections.findIndex((section) => section.id === over.id)

      if (oldIndex !== -1 && newIndex !== -1) {
        // Prevent moving hero/sub_hero sections
        const movingSection = pageSections[oldIndex]
        if (movingSection && (movingSection.layout === "hero" || movingSection.layout === "sub_hero")) {
          console.log("Hero sections cannot be moved from position 0") // eslint-disable-line no-console
          return
        }

        // Prevent moving any section to position 0 (above hero/sub_hero)
        const heroIndex = pageSections.findIndex(
          (section) => section.layout === "hero" || section.layout === "sub_hero"
        )
        if (heroIndex !== -1 && newIndex <= heroIndex) {
          console.log("Cannot move sections above Hero section") // eslint-disable-line no-console
          return
        }

        const newSections = arrayMove(pageSections, oldIndex, newIndex)
        setPageSections(newSections)

        // Update the sections in the context
        try {
          await handleMoveSection(active.id, oldIndex, newIndex)
        } catch (error) {
          // Revert the local state if the server update fails
          setPageSections(sections)
          console.error("Failed to update section order:", error) // eslint-disable-line no-console
        }
      }
    }
  }

  return (
    <div className="overflow-hidden border-b border-gray-light">
      <button
        className={`inline-flex h-14 w-full items-center justify-between px-8 py-4 transition-colors ${
          isCurrentPage ? "border-teal/20 bg-teal/10" : "bg-gray-ultralight hover:bg-gray-light"
        }`}
        onClick={() => {
          onToggle(page.slug)
        }}>
        <span className="flex items-center gap-3 text-left">
          <span className="text-lg font-bold leading-snug">{page.title}</span>
          <span className="text-sm text-gray">({page.sectionsCount} sections)</span>
        </span>
        <div className="flex items-center gap-2">
          {/* Edit page - allowed for all pages (including default), but home page has limited editing */}
          <div
            onClick={(e) => {
              e.stopPropagation()
              setShowEditPageModal(true)
            }}
            className="cursor-pointer rounded p-1 text-gray hover:text-gray-dark"
            title={page.defaultPageType === "home" ? "Edit page (URL cannot be changed)" : "Edit page"}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault()
                e.stopPropagation()
                setShowEditPageModal(true)
              }
            }}>
            <Cog6ToothIcon className="h-4 w-4" />
          </div>

          {/* Delete page - only for custom pages */}
          {!isDefaultPageCheck && (
            <div
              onClick={(e) => {
                e.stopPropagation()
                onDelete(page)
              }}
              className="cursor-pointer rounded p-1 text-gray hover:text-red"
              title="Delete page"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  e.stopPropagation()
                  onDelete(page)
                }
              }}>
              <TrashIcon className="h-4 w-4" />
            </div>
          )}
          {isActive ? <ChevronUpIcon className="h-5 w-5" /> : <ChevronDownIcon className="h-5 w-5" />}
        </div>
      </button>

      <div
        className={`transition-all duration-300 ease-in-out ${
          isActive ? "max-h-[5000px] opacity-100" : "max-h-0 opacity-0"
        }`}>
        <div className="space-y-4">
          {showAddSectionForm && !isDefaultPage && (
            <div className="px-8 py-4">
              <SectionForm
                section={null}
                availableLayouts={availableLayouts}
                onSubmit={handleAddSubmit}
                onCancel={() => setShowAddSectionForm(false)}
              />
            </div>
          )}

          {isPageLoading ? (
            <div className="flex items-center justify-center space-x-2 p-8">
              <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-teal"></div>
              <Typography variant="small" className="text-gray">
                Loading sections...
              </Typography>
            </div>
          ) : pageSections.length === 0 ? (
            <div className="px-8 py-4">
              <Box className="border border-gray-light p-6 text-center">
                <EllipsisVerticalIcon className="mx-auto mb-3 h-8 w-8 text-gray" />
                <Typography variant="body" className="mb-2">
                  No sections yet
                </Typography>
                <Typography variant="small" className="mb-3 text-gray">
                  {isDefaultPageCheck
                    ? `This ${page.title} page has predefined sections that will be loaded automatically.`
                    : `Add sections to build your ${page.title} page.`}
                </Typography>
                {!isDefaultPageCheck && (
                  <button
                    onClick={() => {
                      // First select this page if it's not the current page
                      if (currentPage?.id !== page.id) {
                        onSelectPage(page)
                      }
                      setShowAddSectionScreen(true)
                    }}
                    disabled={isLoading}
                    className="text-center text-teal underline transition-colors hover:text-teal-dark disabled:cursor-not-allowed disabled:opacity-50">
                    + Add First Section
                  </button>
                )}
              </Box>
            </div>
          ) : (
            <div>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext
                  items={pageSections.map((section) => section.id)}
                  strategy={verticalListSortingStrategy}>
                  {pageSections.map((section, index) => (
                    <SortableSectionItem
                      key={section.id}
                      section={section}
                      isSelected={selectedSection?.id === section.id}
                      onSelect={handleSelectSection}
                      onEdit={handleEditClick}
                      onDelete={handleDeleteClick}
                      onToggleHidden={handleToggleHidden}
                      isLast={index === pageSections.length - 1}
                      isDefaultPage={isDefaultPageCheck}
                      sectionIndex={index}
                      pageSections={pageSections}
                    />
                  ))}
                </SortableContext>
              </DndContext>

              <div className="px-8 py-4">
                <button
                  onClick={() => {
                    // First select this page if it's not the current page
                    if (currentPage?.id !== page.id) {
                      onSelectPage(page)
                    }
                    setShowAddSectionScreen(true)
                    setEditingSection(null)
                  }}
                  disabled={isLoading}
                  className="w-full text-center text-teal underline transition-colors hover:text-teal-dark disabled:cursor-not-allowed disabled:opacity-50">
                  + Add Section
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit Page Modal */}
      <AnimatedModal
        visible={showEditPageModal}
        hideModal={() => setShowEditPageModal(false)}
        header="Edit Page"
        showFooter={true}
        actionButtonCopy="Update Page"
        cancelButtonCopy="Cancel"
        onSave={() => {
          // Trigger form submission
          const form = document.querySelector("#edit-page-form")
          if (form) {
            form.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }))
          }
        }}
        saveDisabled={false}>
        <EditPageForm
          page={page}
          onSubmit={(newSlug, title) => {
            onEdit(newSlug, title)
            setShowEditPageModal(false)
          }}
          onCancel={() => setShowEditPageModal(false)}
          isInModal={true}
        />
        {page.defaultPageType === "home" && (
          <div className="mt-4 rounded bg-blue-50 p-3 text-sm text-blue-800">
            The home page URL cannot be changed. This ensures your site always has a consistent home page URL.
          </div>
        )}
      </AnimatedModal>
    </div>
  )
}

const PagesAccordion = ({ onPageToggle }) => {
  const {
    pages,
    handleCreatePage,
    handleUpdatePage,
    handleDeletePage,
    selectPage,
    isLoading,
    setActiveAccordionSection,
    expandedPages,
    setExpandedPages
  } = useWebsiteBuilderV2()
  const [showCreatePageModal, setShowCreatePageModal] = useState(false)
  const [loadingPages, setLoadingPages] = useState(new Set())

  // Separate default and custom pages
  const customPages = pages.filter((page) => !isDefaultPage(page))

  // Check if we can add more custom pages (max 3)
  const canAddCustomPage = customPages.length < 3

  // Order pages: Home, Services, Reviews, [Custom Pages], About, Contact
  const getOrderedPages = () => {
    const orderedPages = []

    // Add default pages in specific order (by default_page_type, not slug)
    const homePage = pages.find((p) => p.defaultPageType === "home")
    const servicesPage = pages.find((p) => p.defaultPageType === "services")
    const reviewsPage = pages.find((p) => p.defaultPageType === "reviews")
    const aboutPage = pages.find((p) => p.defaultPageType === "about")
    const contactPage = pages.find((p) => p.defaultPageType === "contact")

    // Add pages in the desired order
    if (homePage) orderedPages.push(homePage)
    if (servicesPage) orderedPages.push(servicesPage)
    if (reviewsPage) orderedPages.push(reviewsPage)

    // Add custom pages before About and Contact
    orderedPages.push(...customPages)

    if (aboutPage) orderedPages.push(aboutPage)
    if (contactPage) orderedPages.push(contactPage)

    return orderedPages
  }

  const handleTogglePage = async (pageSlug) => {
    const newExpanded = new Set()

    if (expandedPages.has(pageSlug)) {
      // If the page is already expanded, close it
      setLoadingPages((prev) => {
        const newSet = new Set(prev)
        newSet.delete(pageSlug)
        return newSet
      })
    } else {
      // If opening a new page, close all others and open this one
      newExpanded.add(pageSlug)

      // Close Sitewide Styles when opening a page
      setActiveAccordionSection(null)

      // Notify parent component about page toggle
      if (onPageToggle) {
        onPageToggle(pageSlug)
      }

      // Set loading state for this page
      setLoadingPages((prev) => new Set([...prev, pageSlug]))

      // Find the page and select it (which will load sections)
      const page = pages.find((p) => p.slug === pageSlug)
      if (page) {
        try {
          await selectPage(page)
        } finally {
          // Clear loading state after a short delay to ensure sections are loaded
          setTimeout(() => {
            setLoadingPages((prev) => {
              const newSet = new Set(prev)
              newSet.delete(pageSlug)
              return newSet
            })
          }, 500)
        }
      }
    }
    setExpandedPages(newExpanded)
  }

  const handleEditSubmit = async (currentSlug, newSlug, title) => {
    try {
      await handleUpdatePage(currentSlug, title, newSlug)
    } catch (error) {
      // Error handling is done in the context
    }
  }

  const handleDeleteClick = async (page) => {
    if (window.confirm(`Are you sure you want to delete "${page.title}"?`)) {
      try {
        await handleDeletePage(page.slug)
      } catch (error) {
        // Error handling is done in the context
      }
    }
  }

  const handleTogglePageHidden = async (pageSlug, hidden) => {
    try {
      const page = pages.find((p) => p.slug === pageSlug)
      if (page) {
        await handleUpdatePage(pageSlug, page.title, null, hidden)
      }
    } catch (error) {
      // Error handling is done in the context
    }
  }

  return (
    <div className="space-y-0">
      {getOrderedPages().map((page) => (
        <PageAccordion
          key={page.id}
          page={page}
          isActive={expandedPages.has(page.slug)}
          isLoading={loadingPages.has(page.slug)}
          onToggle={handleTogglePage}
          onEdit={(newSlug, title) => handleEditSubmit(page.slug, newSlug, title)}
          onDelete={handleDeleteClick}
          onSelectPage={selectPage}
          onToggleHidden={handleTogglePageHidden}
        />
      ))}

      {canAddCustomPage && (
        <div className="border-t border-gray-light px-8 py-4">
          <button
            onClick={() => setShowCreatePageModal(true)}
            disabled={isLoading}
            className="w-full text-center text-teal underline transition-colors hover:text-teal-dark disabled:cursor-not-allowed disabled:opacity-50">
            + Add Custom Page
          </button>
        </div>
      )}

      {/* Create Page Modal */}
      <AnimatedModal
        visible={showCreatePageModal}
        hideModal={() => setShowCreatePageModal(false)}
        header="Create New Page"
        showFooter={true}
        actionButtonCopy="Create Page"
        cancelButtonCopy="Cancel"
        onSave={() => {
          // Trigger form submission
          const form = document.querySelector("#create-page-form")
          if (form) {
            form.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }))
          }
        }}
        saveDisabled={false}>
        <CreatePageForm
          onSubmit={(title, slug) => {
            handleCreatePage(title, slug)
            setShowCreatePageModal(false)
          }}
          onCancel={() => setShowCreatePageModal(false)}
          isInModal={true}
        />
      </AnimatedModal>
    </div>
  )
}

export default PagesAccordion
